import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import * as styles from '../styles/pages/welcome.css.ts';
import { selectFile } from '../services/fileService';
import { parseCSVToDataset, fetchCSVFromUrl, FetchProgress } from '../services/csvProcessor';
import { saveProject, addRecentFile, listProjects, loadProject, deleteProject } from '../services/storageService';
import { OpenFromUrlDialog } from './OpenFromUrlDialog';
import { createProjectFromDataset, initializeLoadedProject } from '../services/projectService';
import { useProjectStore } from '../stores/projectStore';
import { useConfirmDialogStore } from '../stores/confirmDialogStore';
import { AboutDialog } from './AboutDialog/AboutDialog';
import { TermsDialog } from './AboutDialog/TermsDialog';
import { PrivacyDialog } from './AboutDialog/PrivacyDialog';
import { SettingsModal } from './SettingsModal';
import packageInfo from '../../../package.json';
import { SAMPLE_DATASETS, type SampleDataset } from '../config/sampleDatasets';

/**
 * LauncherView - Launcher screen component dedicated to file selection
 */

interface SavedProject {
  filename: string;
  metadata?: {
    projectName?: string;
    lastModified?: string;
    description?: string;
  };
}

function LauncherView() {
  const confirm = useConfirmDialogStore(state => state.confirm);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showOpenFromUrl, setShowOpenFromUrl] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();


  // ランチャー画面のウィンドウタイトル設定とプロジェクト一覧の読み込み
  useEffect(() => {
    document.title = 'MIDAS - Data Analysis Tool';

    // OPFSの利用可能性をチェック（シークレットモード検出）
    checkOPFSAvailability();

    // OPFSからプロジェクト一覧を読み込み
    loadSavedProjects();
  }, []);

  const checkOPFSAvailability = async () => {
    try {
      // OPFSへのアクセスを試みる
      if (navigator.storage && navigator.storage.getDirectory) {
        await navigator.storage.getDirectory();
      }
    } catch (error: unknown) {
      // シークレットモードでのセキュリティエラーを検出
      if (error instanceof Error && (error.name === 'SecurityError' || error.message?.includes('SecurityError'))) {
        setIsIncognitoMode(true);
        logger.warn('[LauncherView] Running in private/incognito mode - storage features disabled');
      }
    }
  };

  const loadSavedProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const projects = await listProjects();
      setSavedProjects(projects);
      logger.info('[LauncherView] Loaded saved projects:', projects.length);
    } catch (err) {
      logger.error('[LauncherView] Failed to load saved projects:', err);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleOpenFile = async () => {

    try {
      setIsLoading(true);
      setError(null);

      // ファイル選択ダイアログを表示（CSV/TSV/MDSを統合）
      const file = await selectFile('.csv,.tsv,.mds', false) as File | null;

      if (!file) {
        // ユーザーがキャンセル
        setIsLoading(false);
        return;
      }

      logger.info('LauncherView: File selected:', file.name);

      // 拡張子で処理を分岐
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'mds') {
        // MDSプロジェクトファイルを読み込み（gzip圧縮されている）
        const arrayBuffer = await file.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);

        // デシリアライズ（gzip展開、JSONパース、マイグレーション適用）
        const { deserializeMDSFile } = await import('../services/storageService');
        const projectData = await deserializeMDSFile(data);

        // プロジェクトを初期化（完全な構造で）
        const project = initializeLoadedProject(projectData);

        // ストアに保存
        useProjectStore.getState().loadProject(project);

        // OPFSに自動保存（リロード時の復元用、既存があれば上書き）
        logger.info('LauncherView: Auto-saving loaded project to OPFS');
        await saveProject(project);

        // 最近使用したファイルに追加
        await addRecentFile(file.name, file.name, 'mds');

        // プロジェクトビューに遷移
        navigate(`/project/${project.id}`);
      } else {
        // CSV/TSVファイルをパース
        const { dataset, warnings } = await parseCSVToDataset(file);

        if (warnings.length > 0) {
          logger.warn('LauncherView: CSV parsing warnings:', warnings);
        }

        // プロジェクトを作成（完全な構造で）
        const project = createProjectFromDataset(dataset);

        // ストアに保存
        useProjectStore.getState().loadProject(project);

        // OPFSに自動保存（リロード時の復元用）
        logger.info('LauncherView: Auto-saving project to OPFS');
        await saveProject(project);

        // 最近使用したファイルに追加
        await addRecentFile(file.name, file.name, 'csv');

        // プロジェクトビューに遷移
        navigate(`/project/${project.id}`);
      }

    } catch (err) {
      logger.error('LauncherView: Error in handleOpenFile:', err);
      setError(err instanceof Error ? err.message : 'Failed to open file');
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleOpenSavedProject = async (filename: string) => {

    try {
      setIsLoading(true);
      setError(null);

      // OPFSからプロジェクトを読み込み
      const project = await loadProject(filename);

      if (!project) {
        throw new Error('Failed to load project from OPFS');
      }

      // ストアに保存
      useProjectStore.getState().loadProject(project);

      // プロジェクトビューに遷移
      navigate(`/project/${project.id}`);

    } catch (err) {
      logger.error('LauncherView: Error opening saved project:', err);
      setError(err instanceof Error ? err.message : 'Failed to open project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProject = async (filename: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 親要素のクリックイベントを防ぐ

    const confirmed = await confirm({
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project?',
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      testId: 'delete-project-confirm'
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteProject(filename);
      logger.info('[LauncherView] Project deleted:', filename);

      // プロジェクト一覧を更新
      await loadSavedProjects();
    } catch (err) {
      logger.error('[LauncherView] Failed to delete project:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    }
  };

  const handleOpenSampleData = async (dataset: SampleDataset) => {
    try {
      setIsLoading(true);
      setError(null);

      logger.info('[LauncherView] Loading sample dataset:', dataset.name);

      // sample-dataディレクトリからCSVを取得
      const response = await fetch(`/sample-data/${dataset.path}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch sample data: ${response.statusText}`);
      }

      // BlobからFileオブジェクトを作成
      const blob = await response.blob();
      const file = new File([blob], dataset.name + '.csv', { type: 'text/csv' });

      // CSVをパース
      const { dataset: parsedDataset, warnings } = await parseCSVToDataset(file);

      if (warnings.length > 0) {
        logger.warn('[LauncherView] CSV parsing warnings:', warnings);
      }

      // プロジェクトを作成（プロジェクト名にSampleプレフィックスを追加）
      const project = createProjectFromDataset(parsedDataset);
      project.projectName = `Sample: ${dataset.name}`;

      // ストアに保存
      useProjectStore.getState().loadProject(project);

      // OPFSに自動保存
      logger.info('[LauncherView] Auto-saving sample project to OPFS');
      await saveProject(project);

      // プロジェクトビューに遷移
      navigate(`/project/${project.id}`);

    } catch (err) {
      logger.error('[LauncherView] Error loading sample data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sample data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenFromUrl = async (
    url: string,
    options?: { signal?: AbortSignal; onProgress?: (progress: FetchProgress) => void }
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      logger.info('[LauncherView] Loading CSV from URL:', url);

      // URLからCSVを取得
      const { dataset, warnings } = await fetchCSVFromUrl(url, undefined, {
        signal: options?.signal,
        onProgress: options?.onProgress,
      });

      if (warnings.length > 0) {
        logger.warn('[LauncherView] CSV fetch warnings:', warnings);
      }

      // プロジェクトを作成
      const project = createProjectFromDataset(dataset);

      // ストアに保存
      useProjectStore.getState().loadProject(project);

      // OPFSに自動保存
      logger.info('[LauncherView] Auto-saving URL project to OPFS');
      await saveProject(project);

      // プロジェクトビューに遷移
      navigate(`/project/${project.id}`);

    } catch (err) {
      logger.error('[LauncherView] Error loading CSV from URL:', err);
      throw err; // ダイアログ側でエラーを表示するためにre-throw
    } finally {
      setIsLoading(false);
    }
  };

  // 日付フォーマット
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className={styles.launcherContainer} data-testid="launcher-view">
      <header className={styles.header}>
        <div className={styles.headerLogo}>
          <span className={styles.headerLogoText}>MIDAS</span>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.settingsButton}
            onClick={() => setShowAbout(true)}
            title="About MIDAS"
          >
            ℹ️
          </button>
          <button
            className={styles.settingsButton}
            onClick={() => setShowSettings(true)}
            title="Settings"
            data-testid="settings-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14H7M9 8H15M17 16H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={styles.settingsButton}
            onClick={() => navigate('/storage')}
            title="Storage Management"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7M4 7V20C4 20.5523 4.44772 21 5 21H19C19.5523 21 20 20.5523 20 20V7M4 7H20M8 11H16M8 15H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </header>

      <div className={styles.contentWrapper}>
        {/* サイドバー */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarSectionTitle}>
              <span>Quick Access</span>
            </div>
            <div className={styles.sidebarHint}>
              (Browser cache - temporary)
            </div>
          </div>

          {isLoadingProjects ? (
            <div className={styles.sidebarLoading}>
              Loading projects...
            </div>
          ) : savedProjects.length > 0 ? (
            <div className={styles.sidebarProjectsList} data-testid="quick-access-list">
              {savedProjects.slice(0, 5).map((project) => (
                <div
                  key={project.filename}
                  className={styles.sidebarProjectItem}
                  onClick={() => handleOpenSavedProject(project.filename)}
                  data-testid="quick-access-project"
                >
                  <div className={styles.sidebarProjectIcon}>📁</div>
                  <div className={styles.sidebarProjectInfo}>
                    <div className={styles.sidebarProjectName}>
                      {project.metadata?.projectName || project.filename.replace('.mds', '')}
                    </div>
                    {project.metadata?.lastModified && (
                      <div className={styles.sidebarProjectDate}>
                        {formatDate(project.metadata.lastModified).split(' ')[0]}
                      </div>
                    )}
                  </div>
                  <button
                    className={styles.sidebarDeleteButton}
                    onClick={(e) => handleDeleteProject(project.filename, e)}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
              {savedProjects.length > 5 && (
                <div className={styles.sidebarMoreProjects}>
                  +{savedProjects.length - 5} more {savedProjects.length - 5 === 1 ? 'project' : 'projects'}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.sidebarEmpty}>
              No recent projects
            </div>
          )}

          {/* Sample Data Section */}
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarSectionTitle}>
              <span>Sample Data</span>
            </div>
          </div>

          <div className={styles.sidebarProjectsList}>
            {SAMPLE_DATASETS.map((dataset) => (
              <div
                key={dataset.id}
                className={styles.sidebarProjectItem}
                onClick={() => handleOpenSampleData(dataset)}
                title={`${dataset.name}\n${dataset.rows} rows, ${dataset.columns} columns\n${dataset.purpose}`}
                data-testid={`sample-data-${dataset.id}`}
              >
                <div className={styles.sidebarProjectInfo}>
                  <div className={styles.sidebarProjectName}>
                    {dataset.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* メインコンテンツ */}
        <main className={styles.mainArea}>
          <div className={styles.mainContent}>
            <div className={styles.hero}>
              <div className={styles.logoContainer}>
                <div className={styles.logo}>M</div>
              </div>
              <h1 className={styles.appTitle}>Welcome to MIDAS</h1>
              <p className={styles.appVersion}>
                Version {packageInfo.version}
                {import.meta.env.VITE_GIT_COMMIT && import.meta.env.VITE_GIT_COMMIT !== 'unknown' && (
                  <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                    ({import.meta.env.VITE_GIT_COMMIT})
                  </span>
                )}
              </p>
              <div className={styles.legalLinks}>
                <a
                  onClick={() => setShowAbout(true)}
                  className={styles.legalLink}
                >
                  About
                </a>
                <span className={styles.legalSeparator}>•</span>
                <a
                  onClick={() => setShowTerms(true)}
                  className={styles.legalLink}
                >
                  Terms of Service
                </a>
                <span className={styles.legalSeparator}>•</span>
                <a
                  onClick={() => setShowPrivacy(true)}
                  className={styles.legalLink}
                >
                  Privacy Policy
                </a>
              </div>
            </div>

            <div className={styles.fileDropArea}>
              <div className={styles.fileButtonsContainer}>
                <button
                  className={styles.openFileButton}
                  onClick={handleOpenFile}
                  disabled={isLoading}
                >
                  <svg className={styles.fileIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 13H15M12 10V16M21 7V17C21 19.2091 19.2091 21 17 21H7C4.79086 21 3 19.2091 3 17V7C3 4.79086 4.79086 3 7 3H17C19.2091 3 21 4.79086 21 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={styles.fileButtonTitle}>Open File</span>
                  <span className={styles.fileButtonDescription}>
                    Drop here or click to browse
                  </span>
                </button>

                <button
                  className={styles.openFileButton}
                  onClick={() => setShowOpenFromUrl(true)}
                  disabled={isLoading}
                  data-testid="open-from-url-button"
                >
                  <svg className={styles.fileIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.0471 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.47L11.75 5.18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11C13.5705 10.4259 13.0226 9.95083 12.3934 9.60706C11.7642 9.26329 11.0685 9.05886 10.3533 9.00768C9.63816 8.9565 8.92037 9.05969 8.24861 9.31025C7.57685 9.56082 6.96684 9.95294 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.666 2.05659 16.977C2.06798 18.288 2.59382 19.5421 3.52086 20.4691C4.44791 21.3961 5.70197 21.922 7.01295 21.9334C8.32393 21.9448 9.58694 21.4408 10.53 20.53L12.24 18.82" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={styles.fileButtonTitle}>Open from URL</span>
                  <span className={styles.fileButtonDescription}>
                    Load CSV from web
                  </span>
                </button>
              </div>

              {/* 開発モード用: Playwright自動化のためのinput要素 */}
              {import.meta.env.DEV && (
                <input
                  type="file"
                  accept=".csv,.tsv,.mds"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    try {
                      setIsLoading(true);
                      setError(null);

                      const extension = file.name.split('.').pop()?.toLowerCase();

                      if (extension === 'mds') {
                        const arrayBuffer = await file.arrayBuffer();
                        const data = new Uint8Array(arrayBuffer);
                        const { deserializeMDSFile } = await import('../services/storageService');
                        const projectData = await deserializeMDSFile(data);
                        const project = initializeLoadedProject(projectData);
                        useProjectStore.getState().loadProject(project);
                        await saveProject(project);
                        await addRecentFile(file.name, file.name, 'mds');
                        navigate(`/project/${project.id}`);
                      } else {
                        const { dataset, warnings } = await parseCSVToDataset(file);
                        if (warnings.length > 0) {
                          logger.warn('LauncherView: CSV parsing warnings:', warnings);
                        }
                        const project = createProjectFromDataset(dataset);
                        useProjectStore.getState().loadProject(project);
                        await saveProject(project);
                        await addRecentFile(file.name, file.name, 'csv');
                        navigate(`/project/${project.id}`);
                      }
                    } catch (err) {
                      logger.error('LauncherView: Error loading file:', err);
                      setError(err instanceof Error ? err.message : 'Failed to load file');
                      setIsLoading(false);
                    }
                  }}
                  style={{ marginTop: '16px' }}
                  data-testid="dev-file-input"
                />
              )}

              <div className={styles.supportedFormats}>
                <span className={styles.formatLabel}>Supports:</span>
                <span className={styles.formatBadge}>CSV</span>
                <span className={styles.formatBadge}>TSV</span>
                <span className={styles.formatBadge}>MDS</span>
              </div>
            </div>

            <div className={styles.tipSection}>
              <div className={styles.tipIcon}>💡</div>
              <div className={styles.tipText}>
                Always save .mds files to your computer for permanent storage.
                Browser cache is temporary and may be cleared.
              </div>
            </div>

            {/* シークレットモード警告 */}
            {isIncognitoMode && (
              <div className={styles.warningBox}>
                <span className={styles.warningIcon}>⚠️</span>
                <div>
                  <strong>Private/Incognito Mode Detected</strong>
                  <p>
                    File storage features are disabled in private browsing mode.
                    You can still analyze data, but projects cannot be saved or loaded.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            {isLoading && (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading...</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* About Dialog */}
      <AboutDialog
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
      />

      {/* Terms of Service Dialog */}
      <TermsDialog
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />

      {/* Privacy Policy Dialog */}
      <PrivacyDialog
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />

      {/* Open from URL Dialog */}
      <OpenFromUrlDialog
        isOpen={showOpenFromUrl}
        onClose={() => setShowOpenFromUrl(false)}
        onOpen={handleOpenFromUrl}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}

export default LauncherView;